import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { isMapFeatureFlagEnabled } from '@helpers/events';
import { log } from '@core/assistant/providers/Debug';

import {
  blinkTriggerInUnreal,
  teleportToTriggerInUnreal,
} from '../brandingSpotsUnreal';
import { useDrift } from '../DriftContext';
import { useEditorMode } from '../EditorModeContext';
import { KIND_TOKENS, MODE_ICON, MODE_META, modeForEntity } from './kind';
import type { AiEntity } from './types';
import { useAiInspectorStyles, KIND_LABEL_KEY } from './inspectorStyles';

interface IAiInspectorHeaderProps {
  entity: AiEntity;
  onClose: () => void;
}

const AiInspectorHeader = ({ entity, onClose }: IAiInspectorHeaderProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();
  const {
    requestAssistantOpen,
    currentEvent,
    currentRoom,
    requestAssistantRegionChange,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { suppressNextTriggerSelect } = useEditorMode();

  const mapId = currentRoom?.map?.mapId;
  const canTeleportToTrigger = isMapFeatureFlagEnabled(
    currentEvent,
    mapId,
    'TELEPORT_UE_ACTION',
  );
  const canBlinkTrigger = isMapFeatureFlagEnabled(
    currentEvent,
    mapId,
    'RUNTIME_SPOT_UPDATE_UE',
  );
  // Orphaned scripts point at a trigger/region removed from the build, so there
  // is nothing in-world to teleport or blink to.
  const { triggerDrift } = useDrift();
  const isOrphanedTrigger = triggerDrift.rows.some(
    (row) =>
      row.status === 'orphaned' &&
      !!entity.script?.scriptId &&
      row.scriptId === entity.script.scriptId,
  );

  const mode = modeForEntity(entity);
  const tokens = KIND_TOKENS[mode];
  const ModeIcon = MODE_ICON[mode];
  const meta = MODE_META[mode];
  // Trigger/Spot get the colour-coded chip; knowledge falls back to a neutral
  // label so the panel still reads clearly.
  const isColored = mode !== 'knowledge';
  const chipLabel = meta.chipKey
    ? translate(meta.chipKey)
    : translate(KIND_LABEL_KEY[entity.kind].key);

  const handleOpenAssistant = useCallback(() => {
    onClose();
    requestAssistantOpen();
  }, [onClose, requestAssistantOpen]);

  const handleTeleportToTrigger = useCallback(() => {
    if (!entity.primaryTrigger) {
      return;
    }
    log('[editor] teleport to trigger clicked', {
      trigger: entity.primaryTrigger,
    });
    teleportToTriggerInUnreal(sendMessageToUnreal, entity.primaryTrigger);
    // UE's region-enter overlap event doesn't reliably fire for a teleport the
    // way it does for a real walk-in, so the assistant would otherwise stay on
    // the pre-teleport region until the user physically leaves and returns.
    // We already know the destination trigger, so notify it directly. This
    // component renders outside AssistantProvider's tree (sibling of the
    // assistant button), so it goes through MytaverseEventProvider's
    // cross-boundary signal instead of useAssistant().triggerRegionChange.
    requestAssistantRegionChange(entity.primaryTrigger);
    // The Inspector pane occupies the same screen area as Noa — close it so
    // the script/knowledge editor doesn't sit on top of whatever this
    // trigger plays. Provider.tsx's pendingAssistantRegionTrigger replay
    // effect force-opens the assistant as part of handling the region
    // change above (bypassing the branding-mode guard for this deliberate
    // admin action), so no separate requestAssistantOpen() call is needed.
    // The teleport also causes a real in-world region-enter shortly after,
    // which (with "follow live trigger" on) would otherwise re-select this
    // same trigger and reopen the pane we're closing here — suppress that
    // one echo.
    suppressNextTriggerSelect(entity.primaryTrigger);
    onClose();
  }, [
    entity.primaryTrigger,
    sendMessageToUnreal,
    requestAssistantRegionChange,
    suppressNextTriggerSelect,
    onClose,
  ]);

  // Flash this trigger's outline (UE-side duration) to draw the eye to it.
  // Purely a local visual — no teleport, nothing replicated.
  const handleBlinkTrigger = useCallback(() => {
    if (!entity.primaryTrigger) {
      return;
    }
    blinkTriggerInUnreal(sendMessageToUnreal, entity.primaryTrigger);
  }, [entity.primaryTrigger, sendMessageToUnreal]);

  const handleCopy = useCallback(async () => {
    if (!entity.primaryTrigger) {
      return;
    }
    try {
      await navigator.clipboard.writeText(entity.primaryTrigger);
    } catch (copyError) {
      console.warn(
        `Failed to copy identifier: ${(copyError as Error).message}`,
        { trigger: entity.primaryTrigger },
      );
    }
  }, [entity.primaryTrigger]);

  return (
    <Box sx={classes.header}>
      <Box
        sx={[
          classes.headerIcon,
          isColored && {
            backgroundColor: tokens.bg,
            color: tokens.main,
          },
        ]}
      >
        <ModeIcon />
      </Box>
      <Box sx={classes.headerTitleColumn}>
        <Tooltip title={entity.name} placement="bottom" enterDelay={400}>
          <Typography sx={classes.headerTitle}>{entity.name}</Typography>
        </Tooltip>
        <Box sx={classes.headerSubRow}>
          {entity.primaryTrigger ? (
            <Tooltip
              title={entity.primaryTrigger}
              placement="bottom"
              enterDelay={400}
            >
              <Typography component="span" sx={classes.monoIdChip}>
                {entity.primaryTrigger}
                <IconButton
                  size="small"
                  sx={classes.copyButton}
                  onClick={handleCopy}
                  aria-label={translate(
                    'editorMode.copyIdentifier',
                    'Copy identifier',
                  )}
                  data-testid="ai-inspector-copy-id-btn"
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Typography>
            </Tooltip>
          ) : null}
          <Box
            component="span"
            sx={[
              classes.kindChip,
              isColored && {
                color: tokens.text,
                borderColor: tokens.border,
                backgroundColor: tokens.bg,
                '& svg': { color: tokens.main, fontSize: 11 },
              },
            ]}
          >
            <ModeIcon sx={{ fontSize: 11 }} />
            {chipLabel}
          </Box>
          {entity.kind === 'landing' ? (
            <Tooltip
              title={translate(
                'editorMode.aiEntryPointTooltip',
                'Map entry point where user starts their journey',
              )}
              placement="bottom"
              enterDelay={300}
            >
              <Box component="span" sx={classes.entryPointChip}>
                {translate('editorMode.aiEntryPointChip', 'Map entry')}
                <InfoOutlinedIcon />
              </Box>
            </Tooltip>
          ) : null}
          {entity.aliases.length > 0 ? (
            <Tooltip
              title={entity.aliases.join(', ')}
              placement="bottom"
              enterDelay={300}
            >
              <Box component="span" sx={classes.aliasChip}>
                {translate('editorMode.aiAliasChip', {
                  count: entity.aliases.length,
                })}
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      {canTeleportToTrigger && !!entity.primaryTrigger && !isOrphanedTrigger ? (
        <Tooltip
          title={translate('editorMode.teleportToTrigger', 'Teleport to trigger')}
          placement="bottom"
          enterDelay={400}
        >
          <IconButton
            size="small"
            sx={classes.closeButton}
            onClick={handleTeleportToTrigger}
            aria-label={translate(
              'editorMode.teleportToTrigger',
              'Teleport to trigger',
            )}
            data-testid="ai-inspector-teleport-btn"
          >
            <MyLocationOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {canBlinkTrigger && !!entity.primaryTrigger && !isOrphanedTrigger ? (
        <Tooltip
          title={translate('editorMode.blinkTrigger')}
          placement="bottom"
          enterDelay={400}
        >
          <IconButton
            size="small"
            sx={classes.closeButton}
            onClick={handleBlinkTrigger}
            aria-label={translate('editorMode.blinkTrigger', 'Blink trigger')}
            data-testid="ai-inspector-blink-btn"
          >
            <CenterFocusStrongOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip
        title={translate('editorMode.openVirtualAssistant')}
        placement="bottom"
        enterDelay={400}
      >
        <IconButton
          size="small"
          sx={classes.closeButton}
          onClick={handleOpenAssistant}
          aria-label={translate(
            'editorMode.openVirtualAssistant',
            'Open virtual assistant',
          )}
          data-testid="ai-inspector-open-assistant-btn"
        >
          <SmartToyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        sx={classes.closeButton}
        onClick={onClose}
        aria-label={translate('editorMode.close', 'Close')}
        data-testid="ai-inspector-close-btn"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default AiInspectorHeader;
